import React, { useEffect, useRef, useState } from "react"
import { Location, useLocation, useNavigate } from "react-router-dom"
import { connect } from "react-redux"
import { Tooltip } from "react-tooltip"
import { Tooltip as AntdTooltip, Modal } from "antd"
import { ChevronRight } from "react-feather"
import classNames from "classnames"
import { join } from "lodash"

import { NotificationPlacement } from "antd/es/notification/interface"
import API from "../../../../api"
import { ROUTES } from "../../../../routes"
import { mapDispatchProps, mapStateToProps } from "../../../../store/store"
// Hooks
import { useLanguage } from "@languages/index"
import useInfiniteScroll from "../../../../hooks/useInfiniteScroll"
// Helpers
import { isEmpty } from "../../../../helpers/object"
import { debounce } from "../../../../helpers/debounce"
// Components
import Checkbox, { ICON_TYPES } from "../../../../components/checkbox"
import Select from "../../../../components/select"
import RadioButton from "../../../../components/radioButton"
import ModelCard, { TYPES } from "../../../../components/modelCard"
import Paginator from "../../../../components/paginator"
import Button from "../../../../components/button"
import BottomGradient from "../../../../components/bottomGradient"
import OverlayMatchRatingSetting from "components/overlayMatchRatingSetting"
import ModalContactManager from "components/modalContactManager"
import ModalRentalDiscontinued from "components/modalRentalDiscontinued"

import EncourageAllowOtherBrandTagPanel from "pages/notification-panel/EncourageAllowOtherBrandTagPanel"
import BRAND from "constants/brand"
import { isGlovv } from "constants/domains/domains"

const TAB_APPLIED = "APPLIED"
const TAB_APPROVED = "APPROVED"
const TAB_REJECTED = "REJECTED"

const AmbassadorSettings = (props: any) => {
  const language = useLanguage()
  const languageTexts = { ...language.common, ...language.pages.rental }
  const navigate = useNavigate()
  const [inputs, setInputs] = useState<any>({})
  const [isRadioButtonVisible, setIsRadioButtonVisible] = useState<boolean>(false)
  const fixedButtonRef = useRef<HTMLDivElement | null>(null)
  const staticButtonRef = useRef<HTMLDivElement | null>(null)
  const applicantsSectionStartRef = useRef<HTMLDivElement | null>(null)
  const location = useLocation()

  const defaultTabs = [
    {
      name: `${languageTexts.ambassador_index_text_35}`,
      value: TAB_APPLIED,
    },
    {
      name: `${languageTexts.ambassador_index_text_36} (${props.brand.ambassadorCntApproved})`,
      value: TAB_APPROVED,
    },
    {
      name: `${languageTexts.ambassador_index_text_92} (${props.brand.ambassadorCntRejected})`,
      value: TAB_REJECTED,
    },
  ]

  const [tabs, setTabs] = useState<any>(defaultTabs)
  const [selectedTab, setSelectedTab] = useState<any>(defaultTabs[0])

  const maxRentalLimitAmbassadorOptions = [
    { name: languageTexts.unlimited, value: 9999 },
    ...Array.from({ length: 5 }, (_, index) => ({
      name: languageTexts._amount(index + 1),
      value: index + 1,
    })),
  ]

  useEffect(() => {
    props.setInstagram({ user: null })
    fetchBrand()
  }, [])

  useEffect(() => {
    const { hasAgreedGuidelineTerms, rentableUserTypeFl, maxRentalLimitAmbassador } = props.brand
    setInputs({
      userTypeNormal: rentableUserTypeFl.includes(21),
      userTypeAmbassador: rentableUserTypeFl.includes(22),
      maxRentalLimitAmbassador: `${languageTexts._amount(maxRentalLimitAmbassador)}`,
    })
  }, [props.brand])

  useEffect(() => {
    if (location.state && location.state.scrollTo === "applicantsSectionStartRef" && staticButtonRef.current) {
      applicantsSectionStartRef.current.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }, [location.state, location.state?.scrollTo, applicantsSectionStartRef])

  const openNotification = (placement: NotificationPlacement) => {}

  const handleInputChange = (key: string, value: any) => {
    let params: any = {}
    const copy: any = { ...inputs }
    copy[`${key}`] = value

    console.log(`${key} updated`)

    setInputs({ ...inputs, ...copy })

    switch (key) {
      case "userTypeNormal":
        params.userTypeNormal = value
        break

      case "userTypeAmbassador":
        params.userTypeAmbassador = value
        break

      case "maxRentalLimitAmbassador":
        const selected = maxRentalLimitAmbassadorOptions.filter((qty) => {
          return qty.name == value
        })[0]

        params.maxRentalLimitAmbassador = selected.value
        break
    }

    if (!isEmpty(params)) {
      updateBrand(params)
    }
  }

  useEffect(() => {
    const handleScroll = () => {
      if (fixedButtonRef.current && staticButtonRef.current) {
        const staticButton = staticButtonRef.current
        const fixedButton = fixedButtonRef.current

        const staticButtonRect = staticButton.getBoundingClientRect()
        const fixedButtonRect = fixedButton.getBoundingClientRect()
        const windowHeight = window.innerHeight

        const isRadioButtonVisible =
          staticButtonRect.bottom - staticButtonRect.height - props.preferences.headerHeight >= 0 ? false : true
        setIsRadioButtonVisible(isRadioButtonVisible)
      }
    }

    window.addEventListener("scroll", handleScroll)
    return () => {
      window.removeEventListener("scroll", handleScroll)
    }
  }, [props?.preferences?.headerHeight])

  const fetchBrand = async () => {
    await API.Brand.show({ brandToken: props.auth.token })
      .then((response) => {
        props.setBrand(response.data.brand)
        setTabs([
          {
            name: `${languageTexts.ambassador_index_text_35}`,
            value: TAB_APPLIED,
          },
          {
            name: `${languageTexts.ambassador_index_text_36} (${response.data.brand.ambassadorCntApproved})`,
            value: TAB_APPROVED,
          },
          {
            name: `${languageTexts.ambassador_index_text_92} (${response.data.brand.ambassadorCntRejected})`,
            value: TAB_REJECTED,
          },
        ])
      })
      .catch((error) => {
        props.showModal({
          text: languageTexts.ambassador_index_text_3,
        })
      })
  }

  const updateBrand = (params) => {
    API.Brand.update({
      brandToken: props.auth.token,
      ...params,
    })
      .then((response) => {
        openNotification("topRight")
      })
      .catch((error) => {
        props.showModal({ text: error.message })
      })
  }

  const renderToolTipIcon = (tooltipTagId: string, tooltipText: string) => {
    return (
      <>
        <img
          id={tooltipTagId}
          className={"w-[14px] h-[14px]"}
          src={"../../../../assets/icons/ic-question-mark-black.svg"}
        />
        <Tooltip anchorSelect={"#" + tooltipTagId} className="max-w-[360px]" place="top">
          <span className="text-[13px] py-1 whitespace-pre-wrap word-keep-all">{tooltipText}</span>
        </Tooltip>
      </>
    )
  }

  const handleNormalRentableToggle = (confirm?) => {
    const { hasAgreedGuidelineTerms, cards } = props.brand

    if (!inputs?.userTypeNormal && !hasAgreedGuidelineTerms) {
      props.showModal({
        title: languageTexts.ambassador_index_text_51,
        text: languageTexts.ambassador_index_text_52,
        cancel: () => {},
        confirmButtonText: languageTexts.ambassador_index_text_53,
        confirm: () => {
          navigate(ROUTES.RENTAL_REQUIREMENTS_BASIC.PATH)
        },
      })
      return false
    }

    if (props.brand.billingType != BRAND.BILLING_TYPE.FREE && props.brand.pricePerRental > 0 && !cards.length) {
      props.showModal({
        title: languageTexts.ambassador_index_text_5,
        text: languageTexts.ambassador_index_text_6,
        cancel: () => {},

        confirmButtonText: languageTexts.ambassador_index_text_7,
        confirm: () => {
          navigate(ROUTES.CARD_INDEX.PATH)
        },
      })
      return false
    }

    if (!confirm) {
      const title = inputs.userTypeNormal
        ? languageTexts.ambassador_index_text_8
        : languageTexts.ambassador_index_text_9

      const desc = inputs.userTypeNormal
        ? languageTexts._ambassador_index_text_10(props.brand.rentingModelCount)
        : languageTexts.ambassador_index_text_11

      const warn = languageTexts.ambassador_index_text_12

      props.showModal({
        content: () => {
          return (
            <div className="flex flex-col gap-[20px] bg-white p-8">
              <div className="title">
                <span className="font-bold text-[22px] text-black">{title}</span>
              </div>
              <div className="desc">
                <span className="text-md tetx-black">{desc}</span>
              </div>

              <div className="warn text-[#FF0000]">
                <span>{warn}</span>
              </div>

              <div className="buttons flex flex-row columns-2 mt-3 gap-1.5">
                <Button
                  className={"min-w-[10rem] py-3 flex justify-center text-center text-base"}
                  borderColor={"BLACK"}
                  text={languageTexts.cancel}
                  onClick={() => {
                    props.closeModal()
                  }}
                />

                <Button
                  className={"min-w-[10rem] py-3 flex justify-center text-center text-base"}
                  text={props.modal.confirmButtonText ? props.modal.confirmButtonText : languageTexts.confirm}
                  textColor={"WHITE"}
                  buttonColor={"BLACK"}
                  onClick={() => {
                    props.closeModal()
                    handleNormalRentableToggle(true)
                  }}
                />
              </div>
            </div>
          )
        },
      })
      return false
    }

    handleInputChange("userTypeNormal", inputs.userTypeNormal ? 0 : 21)
  }

  const handleAmbassadorRentableToggle = (confirm?) => {
    if (props.brand.isAmbassadorServiceEnabled == false) {
      props.showModal({ text: languageTexts.ambassador_index_text_4 })
      return false
    }

    if (props.brand.ambassadorPhotoRulesCount <= 0) {
      props.showModal({
        title: languageTexts.ambassador_index_text_13,
        text: languageTexts.ambassador_index_text_14,
        cancel: () => {},

        confirmButtonText: languageTexts.ambassador_index_text_15,
        confirm: () => {
          navigate(ROUTES.RENTAL_AMBASSADOR_REQUIREMENTS_POSTING.PATH)
        },
      })

      return false
    }

    if (
      props.brand.billingType != BRAND.BILLING_TYPE.FREE &&
      props.brand.pricePerRentalAmbassador > 0 &&
      !props.brand.cards.length
    ) {
      props.showModal({
        title: languageTexts.ambassador_index_text_16,
        text: languageTexts.ambassador_index_text_17,
        cancel: () => {},

        confirmButtonText: languageTexts.ambassador_index_text_7,
        confirm: () => {
          navigate(ROUTES.CARD_INDEX.PATH)
        },
      })
      return false
    }

    if (!confirm) {
      const title = inputs.userTypeAmbassador
        ? languageTexts.ambassador_index_text_18
        : languageTexts.ambassador_index_text_19

      const desc = inputs.userTypeAmbassador
        ? languageTexts._ambassador_index_text_20(props.brand.rentingAmbassadorCount)
        : languageTexts.ambassador_index_text_21

      const warn = languageTexts.ambassador_index_text_22

      props.showModal({
        content: () => {
          return (
            <div className="flex flex-col gap-[20px] bg-white p-8">
              <div className="title">
                <span className="font-bold text-[22px] text-black">{title}</span>
              </div>
              <div className="desc">
                <span className="text-md tetx-black">{desc}</span>
              </div>

              <div className="warn text-[#FF0000]">
                <span>{warn}</span>
              </div>

              <div className="buttons flex flex-row columns-2 mt-3 gap-1.5">
                <Button
                  className={"min-w-[10rem] py-3 flex justify-center text-center text-base"}
                  borderColor={"BLACK"}
                  text={languageTexts.cancel}
                  onClick={() => {
                    props.closeModal()
                  }}
                />

                <Button
                  className={"min-w-[10rem] py-3 flex justify-center text-center text-base"}
                  text={props.modal.confirmButtonText ? props.modal.confirmButtonText : languageTexts.confirm}
                  textColor={"WHITE"}
                  buttonColor={"BLACK"}
                  onClick={() => {
                    props.closeModal()
                    handleAmbassadorRentableToggle(true)
                  }}
                />
              </div>
            </div>
          )
        },
      })
      return false
    }

    handleInputChange("userTypeAmbassador", inputs.userTypeAmbassador ? 0 : 22)
  }

  const sendBusinessContactRequest = () => {
    const members = props.brand.members.map((member) => {
      return {
        name: member.name,
        role: member.role,
        mobile: member.mobile,
      }
    })

    const params = {
      name: props.brand.name,
      contact: JSON.stringify(members, undefined, 2),
      message: languageTexts.ambassador_index_text_70,
    }

    API.BusinessContact.store(params)

    props.showModal({ text: languageTexts.ambassador_index_text_69 })
  }

  const renderApplicantsRadioButton = () => {
    return (
      <RadioButton
        options={tabs}
        tabClassName={"py-3 bg-gray-100"}
        textClassName={"text-black text-lg tracking-tighter"}
        selected={selectedTab}
        selectedClassName={"py-3 bg-black"}
        selectedTextClassName="font-bold text-lg tracking-tighter"
        selectedTextColor="WHITE"
        onSelect={(filter) => {
          setSelectedTab(filter)
          console.log("filter", filter)
        }}
      />
    )
  }

  return (
    <div
      className="w-full flex flex-col items-center justify-center"
      style={{ paddingTop: props.preferences.headerHeight }}
    >
      <div className="flex flex-col w-full mx-auto max-w-[1024px]">
        <div className="flex flex-col py-[80px]">
          <span className="text-[36px] font-bold">{languageTexts.ambassador_index_text_23}</span>
        </div>

        <div className="flex flex-col w-full gap-[50px]">
          <div className="flex flex-col gap-[15px]">
            <div className="title">
              <span className="text-[22px] font-bold">{languageTexts.ambassador_index_text_24}</span>
            </div>

            <div className="flex flex-row gap-[18px]">
              <div className="flex flex-row items-center gap-[50px]">
                <div className="flex flex-row items-center min-w-[125px] gap-[8px]">
                  <Checkbox
                    text={languageTexts.ambassador_index_text_25}
                    type={ICON_TYPES.DEFAULT}
                    checked={inputs.userTypeNormal}
                    onClick={() => {
                      handleNormalRentableToggle()
                    }}
                  />
                  {renderToolTipIcon(
                    "common-model-tooltip",
                    languageTexts._ambassador_index_text_26(props.settings.currencyText)
                  )}
                </div>
                <div className="relative flex flex-row items-center min-w-[125px] gap-[8px]">
                  <Checkbox
                    text={languageTexts.ambassador_index_text_27}
                    type={ICON_TYPES.DEFAULT}
                    checked={inputs.userTypeAmbassador}
                    onClick={() => {
                      handleAmbassadorRentableToggle()
                    }}
                  />
                  {renderToolTipIcon(
                    "ambassador-model-tooltip",
                    languageTexts._ambassador_index_text_28(props.settings.currencyText)
                  )}
                  <div className="absolute left-1/2 top-5">
                    <AntdTooltip
                      placement="bottom"
                      title={<span className="px-2 text-[13px]">{languageTexts.ambassador_index_text_29}</span>}
                      open={true}
                      color="#000000"
                      zIndex={1}
                      overlayInnerStyle={{
                        borderRadius: "50px",
                        marginTop: "-1px",
                      }}
                    ></AntdTooltip>
                  </div>
                </div>
                <div className="mx-auto"></div>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-[15px] mt-5">
            <div className="title">
              <span className="text-[22px] font-bold">{languageTexts.ambassador_index_text_54}</span>
            </div>

            <div className="flex flex-row gap-[18px]">
              <div
                className="flex flex-row items-center gap-[50px]  cursor-pointer"
                onClick={() => {
                  navigate(ROUTES.RENTAL_REQUIREMENTS_BASIC.PATH)
                }}
              >
                <span className="font-bold text-[15px] text-[#000000] underline">
                  {languageTexts.ambassador_index_text_55}
                </span>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-[15px] mt-5">
            <div className="title">
              <span className="text-[22px] font-bold">{languageTexts.ambassador_index_text_30}</span>
            </div>

            <div className="flex flex-row gap-[18px]">
              <div className="flex flex-row items-center gap-[50px] min-w-[208px]">
                <Select
                  bordered
                  options={maxRentalLimitAmbassadorOptions}
                  placeholder={undefined}
                  value={inputs.maxRentalLimitAmbassador}
                  onChange={(value) => {
                    const selected = maxRentalLimitAmbassadorOptions.filter((qty) => {
                      return qty.name == value
                    })[0]

                    handleInputChange("maxRentalLimitAmbassador", selected.name)
                  }}
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-[15px]">
            <div className="title">
              <span className="text-[22px] font-bold">{languageTexts.ambassador_index_text_33}</span>
            </div>
            <p>{languageTexts.ambassador_index_text_56()}</p>
            <div ref={applicantsSectionStartRef} className="flex flex-row gap-[18px]">
              <div
                className="flex flex-row items-center gap-[50px] cursor-pointer"
                onClick={() => {
                  if (props.brand.isAmbassadorServiceEnabled == false) {
                    sendBusinessContactRequest()
                    return false
                  }

                  navigate(ROUTES.RENTAL_AMBASSADOR_REQUIREMENTS_POSTING.PATH)
                }}
              >
                <span className="font-bold text-[15px] text-[#000000] underline">
                  {languageTexts.ambassador_index_text_57}
                </span>
              </div>
              <div
                className="flex flex-row items-center gap-[50px] cursor-pointer"
                onClick={() => {
                  if (props.brand.isAmbassadorServiceEnabled == false) {
                    sendBusinessContactRequest()
                    return false
                  }

                  navigate(ROUTES.RENTAL_AMBASSADOR_REQUIREMENTS_VIDEO.PATH)
                }}
              >
                <span className="font-bold text-[15px] text-[#000000] underline">
                  {languageTexts.ambassador_index_text_58}
                </span>
              </div>
            </div>
          </div>

          <div className="ratio-button-section flex flex-col gap-[30px]">
            <div ref={staticButtonRef} className="applicants-radio-button-static">
              {renderApplicantsRadioButton()}
            </div>
            <div
              ref={fixedButtonRef}
              className="applicants-radio-button-fixed fixed left-0 right-0 mx-auto max-w-[1024px] z-[1]"
              style={{
                top: `${props.preferences.headerHeight}px`,
                opacity: isRadioButtonVisible ? 1 : 0,
                // transition: "opacity 0.5s"
              }}
            >
              {renderApplicantsRadioButton()}
            </div>

            <div>
              <Button
                onClick={() => {
                  props.showModal({
                    content: () => (
                      <ModalRentalDiscontinued closeModal={props.closeModal} />
                    )
                  })
                }}
                className="bg-[#FF0000]"
                renderChildren={() => (
                  <div className="py-[20px] px-[24px] w-full flex flex-row justify-between font-bold text-white">
                    <div>2025년 9월 26일부로 제품 대여 서비스를 종료합니다.</div>
                    <div>자세히 보기 →</div>
                  </div>
                )}
              />
            </div>
            
            {selectedTab && selectedTab.value == TAB_APPLIED && <AmbassadorBudget {...props} />}

            {props.brand.hasDisallowedOtherBrandTag ? <EncourageAllowOtherBrandTagPanel {...props} /> : null}

            {selectedTab && selectedTab.value == tabs[0]?.value ? (
              <AmbassadorApplicantsAppliedSection
                {...props}
                isVisible={selectedTab && selectedTab.value == tabs[0]?.value}
                option={tabs[0].value}
                tabs={tabs}
                setTabs={setTabs}
                selectedTab={selectedTab}
                fetchBrand={fetchBrand}
              />
            ) : null}

            {selectedTab && selectedTab.value == tabs[1]?.value ? (
              <AmbassadorApplicantsSection
                {...props}
                isVisible={selectedTab && selectedTab.value == tabs[1]?.value}
                option={tabs[1].value}
                tabs={tabs}
                setTabs={setTabs}
                selectedTab={selectedTab}
                fetchBrand={fetchBrand}
              />
            ) : null}

            {selectedTab && selectedTab.value == tabs[2]?.value ? (
              <AmbassadorApplicantsSection
                {...props}
                isVisible={selectedTab && selectedTab.value == tabs[2]?.value}
                option={tabs[2].value}
                tabs={tabs}
                setTabs={setTabs}
                selectedTab={selectedTab}
                fetchBrand={fetchBrand}
              />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}

const AmbassadorApplicantsSection = (props) => {
  const { fetchBrand, option, selectedTab, isVisible } = props

  const language = useLanguage()
  const languageTexts = { ...language.common, ...language.pages.rental }

  const [applicants, setApplicants] = useState<any>(undefined)
  const [totalApplicantsCount, setTotalApplicantsCount] = useState(0)
  const [selectedOrder, setSelectedOrder] = useState<any>({
    name: languageTexts.ambassador_index_text_94,
    column: "connected_at",
    order: "desc",
    value: 1,
    tooltip: languageTexts._ambassador_index_text_95(),
    tooltipWidth: 360,
  })
  const [filterIsRented, setFilterRentedBefore] = useState(false)
  const [filterIsFavorite, setFilterLiked] = useState(false)
  const [filterIsSuggested, setFilterSuggested] = useState(false)
  const [searchFemale, setSearchFemale] = useState(props.brand.availableSex.includes(1))
  const [searchMale, setSearchMale] = useState(props.brand.availableSex.includes(2))
  const [currentPage, setCurrentPage] = useState<any>(1)
  const [paginator, setPaginator] = useState<any>()
  const [selected, setSelected]: any = useState([])

  const [isFetching, setIsFetching, isEnded, setIsEnded] = useInfiniteScroll(async () => {})

  useEffect(() => {
    setSelected([])
  }, [selectedTab, selectedOrder?.column, filterIsRented, filterIsFavorite, filterIsSuggested])

  useEffect(() => {
    console.log("selected", selected)
  }, [selected])

  const refresh = () => {
    setApplicants([])
    debounce(() => {
      fetchApplicants({ refresh: true })
    }, 200)
  }

  useEffect(() => {
    if (applicants != undefined) {
      refresh()
    }
  }, [selectedOrder])

  useEffect(() => {
    if (applicants != undefined) {
      refresh()
    }
  }, [filterIsRented, filterIsFavorite, filterIsSuggested, searchFemale, searchMale])

  useEffect(() => {
    fetchApplicants()
  }, [])

  const fetchApplicants = async (config?) => {
    setIsFetching(true)
    setApplicants([])

    let targetPage = config?.page ? config.page : currentPage
    if (config?.refresh) targetPage = 1

    const params: any = {
      brandToken: props.auth.token,
      status: props.option,
      page: targetPage,
      with: join(["likes"], ","),
      isRented: filterIsRented ? true : null,
      isFavorite: filterIsFavorite ? true : null,
      isSuggested: filterIsSuggested ? true : null,
      orderColumn: selectedOrder ? selectedOrder.column : "likes_by_brands",
      orderValue: selectedOrder ? selectedOrder.order : "desc",
      sex: join([searchFemale ? 1 : 0, searchMale ? 2 : 0], ","),
    }

    await API.Ambassador.index(params)
      .then((response) => {
        const _paginator = response.data.paginator
        setApplicants(() => {
          return response.data.applicants
        })

        setIsEnded(_paginator.currentPage >= _paginator.lastPage)
        setPaginator(_paginator)
        setCurrentPage(targetPage)
        setIsFetching(false)
      })
      .catch((error) => {
        props.showModal({ text: error.message })
      })
  }

  const updateApplicants = async (params?: any) => {
    try {
      return await API.Ambassador.update(params)
    } catch (error) {
      props.showModal({ text: error.message })
    }
  }

  const onApplicantsUpdated = async () => {
    await fetchBrand()
    await fetchApplicants({ refresh: true })
  }

  return (
    <>
      <div
        className="pb-24 flex flex-col min-h-[950px]"
        style={{
          display: isVisible ? "block" : "none",
        }}
      >
        <AmbassadorApplicantsHeader
          {...props}
          selectedTab={selectedTab}
          selectedOrder={selectedOrder}
          filterIsRented={filterIsRented}
          filterIsFavorite={filterIsFavorite}
          filterIsSuggested={filterIsSuggested}
          isFilterIsSuggestedEnabled={true}
          searchMale={searchMale}
          searchFemale={searchFemale}
          setSelectedOrder={setSelectedOrder}
          setFilterRentedBefore={setFilterRentedBefore}
          setFilterLiked={setFilterLiked}
          setFilterSuggested={setFilterSuggested}
          setSearchMale={setSearchMale}
          setSearchFemale={setSearchFemale}
        />

        <AmbassadorApplicantsList
          {...props}
          isFetching={isFetching}
          applicants={applicants}
          setApplicants={setApplicants}
          filterIsRented={filterIsRented}
          filterIsFavorite={filterIsFavorite}
          filterIsSuggested={filterIsSuggested}
          totalApplicantsCount={totalApplicantsCount}
          setTotalApplicantsCount={setTotalApplicantsCount}
          updateApplicants={updateApplicants}
          onApplicantsUpdated={onApplicantsUpdated}
          selected={selected}
          setSelected={setSelected}
        />

        {paginator ? (
          <Paginator
            currentPage={currentPage}
            lastPage={paginator.lastPage}
            onPageChange={(page) => {
              fetchApplicants({ page: page })
            }}
          />
        ) : null}
      </div>
    </>
  )
}

const AmbassadorApplicantsAppliedSection = (props) => {
  const { fetchBrand, option, selectedTab, isVisible } = props

  const language = useLanguage()
  const languageTexts = { ...language.common, ...language.pages.rental }
  const [applicants, setApplicants] = useState<any>(undefined)
  const [totalApplicantsCount, setTotalApplicantsCount] = useState(0)
  const [userSuggestionsCount, setUserSuggestionsCount] = useState(0)
  const [selectedOrder, setSelectedOrder] = useState<any>({
    name: languageTexts.ambassador_index_text_89,
    column: "brands_selected_frequently_count",
    order: "desc",
    value: 7,
    tooltip: languageTexts._ambassador_index_text_91(),
    tooltipWidth: 360,
  })
  const [filterIsRented, setFilterRentedBefore] = useState(false)
  const [filterIsFavorite, setFilterLiked] = useState(false)
  const [filterIsSuggested, setFilterSuggested] = useState(false)
  const [isFilterIsSuggestedEnabled, setIsFilterIsSuggestedEnabled] = useState(true)
  const [searchFemale, setSearchFemale] = useState(props.brand.availableSex.includes(1))
  const [searchMale, setSearchMale] = useState(props.brand.availableSex.includes(2))

  const [selected, setSelected]: any = useState([])
  const [isFetching, setIsFetching] = useState(true)
  const [isFetchingInitialParams, setIsFetchingInitialParams] = useState(true)
  const matchSelectedStyles = useRef<any[]>([])
  const matchSelectedCategories = useRef<any[]>([])
  const matchBannedCategories = useRef<any[]>([])
  const [matchFiltersUpdatedAt, setMatchFiltersUpdatedAt] = useState(null)

  useEffect(() => {
    setSelected([])
  }, [
    selectedTab,
    selectedOrder?.column,
    filterIsRented,
    filterIsFavorite,
    filterIsSuggested,
    matchFiltersUpdatedAt,
    searchFemale,
    searchMale,
  ])

  const refresh = () => {
    setApplicants([])
    debounce(() => {
      fetchApplicants({ refresh: true })
    }, 200)
  }

  useEffect(() => {
    if (applicants != undefined) {
      refresh()
    }
  }, [selectedOrder, filterIsRented, filterIsFavorite, filterIsSuggested, searchFemale, searchMale])

  useEffect(() => {
    if (isFetchingInitialParams === false) {
      fetchApplicants()
    }
  }, [isFetchingInitialParams])

  useEffect(() => {
    ;(async () => {
      await fetchMatchRatings()
      await fetchInitialParams()
      setIsFetchingInitialParams(false)
    })()
  }, [])

  const fetchInitialParams = async (config?) => {
    try {
      const params: any = {
        brandToken: props.auth.token,
        status: props.option,
        with: join(["likes"], ","),
        isRented: null,
        isFavorite: null,
        isSuggested: true, // 추천모델 숫자 체크
        hasAgreedAmbassadorTerms: true,
        sex: join([searchFemale ? 1 : 0, searchMale ? 2 : 0], ","),
      }
      params.limit = 1
      params.needTotal = true

      const res = await API.User.index(params)

      const total = res.data.total
      if (total > 0) {
        setFilterSuggested(true)
        setUserSuggestionsCount(total)
        setSelectedOrder({
          name: languageTexts.ambassador_index_text_94,
          column: "connected_at",
          order: "desc",
          value: 1,
          tooltip: languageTexts._ambassador_index_text_95(),
          tooltipWidth: 360,
        })
        return
      }
      // setIsFilterIsSuggestedEnabled(false)
    } catch (e) {
      props.showModal({ text: e.message })
    }
  }

  const fetchApplicants = async (config?) => {
    setIsFetching(true)
    setApplicants([])

    let newApplicants = []
    let newTotal = 0
    const BASE_LIMIT = 5

    const params: any = {
      brandToken: props.auth.token,
      status: props.option,
      with: join(["likes"], ","),
      isRented: filterIsRented ? true : null,
      isFavorite: filterIsFavorite ? true : null,
      isSuggested: filterIsSuggested ? true : null,
      orderColumn: selectedOrder ? selectedOrder.column : "likes_by_brands",
      orderValue: selectedOrder ? selectedOrder.order : "desc",
      hasAgreedAmbassadorTerms: true,
      sex: join([searchFemale ? 1 : 0, searchMale ? 2 : 0], ","),
    }

    try {
      params.preferredCategoryIds = matchSelectedCategories.current.map((ms, i) => ms.id)
      params.bannedCategoryIds = matchBannedCategories.current.map((ms, i) => ms.id)
      params.limit = BASE_LIMIT
      params.hasRecentConnection = true
      params.needTotal = true
      const categoryRestrictedResponse = await API.User.index(params)

      categoryRestrictedResponse.data.users.forEach((user) => {
        const { id } = user
        user.user = { ...user, id, userId: id }
        user.id = id
        user.userId = id
      })

      newApplicants.push(...categoryRestrictedResponse.data.users)
      newTotal = categoryRestrictedResponse.data.total
      // unlimit selected categories + recent connection
      if (newApplicants.length < Math.min(BASE_LIMIT, newTotal) && params.preferredCategoryIds.length > 0) {
        params.preferredCategoryIds = []
        params.limit = BASE_LIMIT - newApplicants.length
        params.needTotal = false
        params.exceptUserIds = newApplicants.map((na, i) => na.id)
        params.hasRecentConnection = true
        const noSelectedCategoryRestrictedResponse = await API.User.index(params)

        noSelectedCategoryRestrictedResponse.data.users.forEach((user) => {
          const { id } = user
          user.user = { ...user, id, userId: id }
          user.id = id
          user.userId = id
        })
        newApplicants.push(...noSelectedCategoryRestrictedResponse.data.users)
      }
      // unlimit banned categories + recent connection
      if (newApplicants.length < Math.min(BASE_LIMIT, newTotal) && params.bannedCategoryIds.length > 0) {
        params.bannedCategoryIds = []
        params.limit = BASE_LIMIT - newApplicants.length
        params.needTotal = false
        params.exceptUserIds = newApplicants.map((na, i) => na.id)
        params.hasRecentConnection = true
        const noRestrictedResponse = await API.User.index(params)

        noRestrictedResponse.data.users.forEach((user) => {
          const { id } = user
          user.user = { ...user, id, userId: id }
          user.id = id
          user.userId = id
        })
        newApplicants.push(...noRestrictedResponse.data.users)
      }
      // remains
      if (newApplicants.length < Math.min(BASE_LIMIT, newTotal)) {
        params.limit = BASE_LIMIT - newApplicants.length
        params.needTotal = false
        params.exceptUserIds = newApplicants.map((na, i) => na.id)
        params.hasRecentConnection = undefined
        const remainResponse = await API.User.index(params)

        remainResponse.data.users.forEach((user) => {
          const { id } = user
          user.user = { ...user, id, userId: id }
          user.id = id
          user.userId = id
        })
        newApplicants.push(...remainResponse.data.users)
      }

      setApplicants(() => {
        return newApplicants.slice(0, BASE_LIMIT)
      })

      setIsFetching(false)
    } catch (error) {
      props.showModal({ text: error.message })
    }
  }

  const fetchMatchRatings = async () => {
    try {
      const response = await API.MatchRating.index({
        brandToken: props.auth.token,
        brandId: props.brand.id,
        with: ["category", "style"],
      })
      const newMatchSelectedCategories = []
      const newMatchBannedCategories = []
      for (const matchRating of response.data.matchRatings) {
        if (matchRating.rating == null || matchRating.rating == 0) {
          continue
        }
        if (matchRating.categoryId != null && matchRating.category != null && matchRating.rating > 0) {
          matchRating.category["rating"] = matchRating.rating
          newMatchSelectedCategories.push(matchRating.category)
          continue
        }
        if (matchRating.categoryId != null && matchRating.category != null && matchRating.rating < 0) {
          matchRating.category["rating"] = matchRating.rating
          newMatchBannedCategories.push(matchRating.category)
          continue
        }
      }
      matchSelectedCategories.current = newMatchSelectedCategories
      matchBannedCategories.current = newMatchBannedCategories
      setMatchFiltersUpdatedAt(Date.now())
    } catch (error) {
      props.showModal({ text: error.message })
    }
  }

  const updateApplicants = async (params?: any) => {
    try {
      return await API.Ambassador.update(params)
    } catch (error) {
      props.showModal({ text: error.message })
    }
  }

  const onApplicantsUpdated = async () => {
    await fetchBrand()
    await fetchApplicants({ refresh: true })
  }

  return (
    <>
      <div
        className="pb-24 flex flex-col min-h-[950px]"
        style={{
          display: isVisible ? "block" : "none",
        }}
      >
        <AmbassadorApplicantsHeader
          {...props}
          selectedTab={selectedTab}
          selectedOrder={selectedOrder}
          filterIsRented={filterIsRented}
          filterIsFavorite={filterIsFavorite}
          filterIsSuggested={filterIsSuggested}
          isFilterIsSuggestedEnabled={isFilterIsSuggestedEnabled}
          searchMale={searchMale}
          searchFemale={searchFemale}
          setSelectedOrder={setSelectedOrder}
          setFilterRentedBefore={setFilterRentedBefore}
          setFilterLiked={setFilterLiked}
          setFilterSuggested={setFilterSuggested}
          setSearchMale={setSearchMale}
          setSearchFemale={setSearchFemale}
          setMatchStylesAndCategories={(styles, categories) => {
            const selectedCategories = []
            const bannedCategories = []
            for (let category of categories) {
              if (category.rating > 0) selectedCategories.push(category)
              if (category.rating < 0) bannedCategories.push(category)
            }
            matchSelectedCategories.current = selectedCategories
            matchBannedCategories.current = bannedCategories
            setMatchFiltersUpdatedAt(Date.now())

            if (applicants != undefined) {
              refresh()
            }
          }}
        />

        <AmbassadorApplicantsList
          {...props}
          selectedTab={selectedTab}
          isFetching={isFetching}
          applicants={applicants}
          setApplicants={setApplicants}
          filterIsRented={filterIsRented}
          filterIsFavorite={filterIsFavorite}
          filterIsSuggested={filterIsSuggested}
          totalApplicantsCount={totalApplicantsCount}
          setTotalApplicantsCount={setTotalApplicantsCount}
          userSuggestionsCount={userSuggestionsCount}
          updateApplicants={updateApplicants}
          onApplicantsUpdated={onApplicantsUpdated}
          selected={selected}
          setSelected={setSelected}
        />
      </div>
    </>
  )
}

const AmbassadorApplicantsHeader = (props) => {
  const language = useLanguage()
  const languageTexts = { ...language.common, ...language.pages.rental }

  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-[30px] md:flex-col justify-between">
        {props.selectedTab.value === TAB_APPLIED &&
          (props.brand.isCategoryFashion && isGlovv() == false ? (
            <div className="flex flex-row justify-start">
              <div
                className="flex flex-row flex-shrink items-center gap-2 font-bold cursor-pointer"
                onClick={() => {
                  props.showOverlays({
                    content: () => {
                      return (
                        <OverlayMatchRatingSetting
                          {...props}
                          hasCloseButton={true}
                          shouldShowCautionModal={true}
                          onCompleteSubmit={(styles, categories) => {
                            props.setMatchStylesAndCategories(styles, categories)
                          }}
                        />
                      )
                    },
                  })
                }}
              >
                <span>{languageTexts.ambassador_index_text_93}</span>
                <ChevronRight color="#000" size={20} />
              </div>
            </div>
          ) : null)}
        <div id="group-filters">
          <div className="flex flex-row gap-4">
            <Checkbox
              text={languageTexts.ambassador_index_text_39}
              checked={props.filterIsRented}
              onClick={() => {
                props.setFilterRentedBefore(!props.filterIsRented)
              }}
            />

            <Checkbox
              text={languageTexts.ambassador_index_text_40}
              checked={props.filterIsFavorite}
              onClick={() => {
                props.setFilterLiked(!props.filterIsFavorite)
              }}
            />

            {/* 남성 */}
            <Checkbox
              text={languageTexts.male}
              checked={props.searchMale}
              onClick={() => {
                props.setSearchMale(!props.searchMale)
              }}
            />

            {/* 여성 */}
            <Checkbox
              text={languageTexts.female}
              checked={props.searchFemale}
              onClick={() => {
                props.setSearchFemale(!props.searchFemale)
              }}
            />
          </div>
          <div className="mt-[10px] flex flex-row gap-4">
            {/* 오늘룩 추천모델 */}
            <div className="flex flex-row items-center gap-[6px]">
              <Checkbox
                text={languageTexts.ambassador_index_text_87}
                textClassName={classNames({
                  "text-slate-400": props.isFilterIsSuggestedEnabled !== true,
                })}
                checked={props.filterIsSuggested}
                onClick={() => {
                  if (props.isFilterIsSuggestedEnabled !== true) {
                    return
                  }
                  props.setFilterSuggested(!props.filterIsSuggested)
                }}
              />
              <div className="px-[8px] py-[4px] text-white bg-[#FF0099] text-[12px] font-bold rounded-full select-none">
                {languageTexts.ambassador_index_text_98}
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-row">
          <div>
            <RadioButton
              className="gap-6"
              dots={true}
              borderless={true}
              selected={props.selectedOrder}
              singleLine={true}
              options={[
                {
                  name: languageTexts.ambassador_index_text_94,
                  column: "connected_at",
                  order: "desc",
                  value: 1,
                  tooltip: languageTexts._ambassador_index_text_95(),
                  tooltipWidth: 360,
                },
                // {
                //   name: languageTexts.ambassador_index_text_41,
                //   column: "created_at",
                //   order: "desc",
                //   value: 2,
                // },
                {
                  name: languageTexts.ambassador_index_text_45,
                  column: "likes_by_brands",
                  order: "desc",
                  value: 3,
                },
                {
                  name: languageTexts.ambassador_index_text_43,
                  column: "followers",
                  order: "desc",
                  value: 4,
                },
                {
                  name: languageTexts.ambassador_index_text_44,
                  column: "likes",
                  order: "desc",
                  value: 5,
                },
                {
                  name: languageTexts.ambassador_index_text_88,
                  column: "brands_in_ambassador_role_count",
                  order: "desc",
                  value: 6,
                  tooltip: languageTexts._ambassador_index_text_90(),
                  tooltipWidth: 360,
                },
                {
                  name: languageTexts.ambassador_index_text_89,
                  column: "brands_selected_frequently_count",
                  order: "desc",
                  value: 7,
                  tooltip: languageTexts._ambassador_index_text_91(),
                  tooltipWidth: 360,
                },
              ]}
              onSelect={(option) => {
                props.setSelectedOrder(option)
              }}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

const AmbassadorApplicantsList = (props) => {
  const language = useLanguage()
  const languageTexts = { ...language.common, ...language.pages.rental }
  const {
    selectedTab,
    applicants,
    totalApplicantsCount,
    userSuggestionsCount,
    filterIsRented,
    filterIsFavorite,
    filterIsSuggested,
    setApplicants,
    updateApplicants,
    isFetching,
    selected,
    setSelected,
  } = props

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isVisibleModalContactManager, setIsVisibleModalContactManager] = useState(false)

  if (applicants === undefined || totalApplicantsCount == undefined || (isFetching && applicants.length == 0)) {
    return (
      <div className="flex flex-row justify-center text-center my-[80px]">
        <span className="prose prose-xl text-slate-400">{languageTexts.loading_1}</span>
      </div>
    )
  }

  if (applicants && !applicants.length && !isFetching) {
    let text = ``
    switch (selectedTab?.value) {
      case TAB_APPLIED:
        text = languageTexts._ambassador_index_text_68(totalApplicantsCount)
        break
      case TAB_APPROVED:
        text = languageTexts._ambassador_index_text_96(totalApplicantsCount)
        break
      case TAB_REJECTED:
        text = languageTexts._ambassador_index_text_97(totalApplicantsCount)
        break
    }

    if ((filterIsRented || filterIsFavorite || filterIsSuggested) && totalApplicantsCount == 0 && !isFetching) {
      text = languageTexts.ambassador_index_text_47
    }

    return (
      <div className="flex flex-col gap-1 justify-center text-center my-[80px]">
        <span className="font-bold text-sm tracking-tighter">{`${text}`}</span>
      </div>
    )
  }

  const handleSelect = (application: any) => {
    const isSelected = selected.includes(application.userId)
    if (isSelected) {
      setSelected(selected.filter((item) => item !== application.userId))
    } else {
      setSelected([...selected, application.userId])
    }
  }

  const submit = async () => {
    if (isFetching || isSubmitting) {
      props.showModal({ text: languageTexts.ambassador_index_text_71 })
      return false
    }
    setIsSubmitting(true)

    let message
    let hasFirstAmbassador = false

    try {
      let approvedCount = 0
      let rejectedCount = 0

      for (const applicant of applicants) {
        let params = {
          brandToken: props.auth.token,
          applicantId: applicant.id,
          brandId: props.brand.id,
          userId: applicant.userId,
        }
        if (selected.indexOf(applicant.userId) >= 0) {
          params["action"] = "approved"
          approvedCount += 1
        } else {
          rejectedCount += 1
          params["action"] = "rejected"
        }
        let res = await updateApplicants(params)

        message = res?.message
        if (res?.data?.isFirst) {
          hasFirstAmbassador = true
        }
      }

      props.showModal({
        hideCloseButton: true,
        couldCloseWithBackgroundDimmer: true,
        title: languageTexts.ambassador_index_text_37,
        text: `선정: ${approvedCount}명\n미선정: ${rejectedCount}명`,
        // confirm: () => {
        //   if (hasFirstAmbassador) {
        //     debounce(() => {
        //       props.showModal({
        //         title: languageTexts.ambassador_index_text_72,
        //         text: languageTexts.ambassador_index_text_73,
        //       })
        //     }, 400)
        //   }
        // },
      })
    } catch (error) {
      props.showModal({ text: error.message })
    }

    setSelected([])
    props.onApplicantsUpdated ? props.onApplicantsUpdated() : null
    setIsSubmitting(false)
  }

  const approveSelectedAmbassadors = async () => {
    if (isFetching) {
      props.showModal({ text: languageTexts.ambassador_index_text_71 })
      return false
    }

    let response = undefined
    let hasFirstAmbassador = false

    for (const userId of selected) {
      const applicant = applicants.find((applicant) => applicant.userId == userId)
      const params = {
        brandToken: props.auth.token,
        applicantId: applicant.id,
        brandId: props.brand.id,
        userId: userId,
        action: "approved",
      }

      let updateApplicantsResponse = await updateApplicants(params)

      if (!response) {
        response = updateApplicantsResponse
        if (response?.data?.isFirst) {
          hasFirstAmbassador = true
        }
      }
    }

    setSelected([])
    props.onApplicantsUpdated ? props.onApplicantsUpdated() : null

    if (response?.message) {
      props.showModal({
        text: response.message,
        confirm: () => {
          if (hasFirstAmbassador) {
            debounce(() => {
              props.showModal({
                title: languageTexts.ambassador_index_text_72,
                text: languageTexts.ambassador_index_text_73,
              })
            }, 400)
          }
        },
      })
    }
  }

  const rejectSelectedAmbassadors = async () => {
    if (isFetching) {
      props.showModal({ text: languageTexts.ambassador_index_text_71 })
      return false
    }

    let response = undefined
    for (const userId of selected) {
      const params = {
        brandToken: props.auth.token,
        brandId: props.brand.id,
        userId: userId,
        action: "rejected",
      }

      let updateApplicantsResponse = await updateApplicants(params)

      if (!response) {
        response = updateApplicantsResponse
      }
    }

    setSelected([])
    props.onApplicantsUpdated ? props.onApplicantsUpdated() : null

    if (response?.message) {
      props.showModal({
        text: response.message,
      })
    }
  }

  const requestSelectionToAdmin = async () => {
    try {
      await API.Ambassador.requestSelectionToAdmin({
        brandToken: props.auth.token,
      })
      setIsVisibleModalContactManager(true)
    } catch (e) {
      props.showModal({
        text: e.message,
      })
    }
  }

  const showRejectConfirmModal = () => {
    if (!selected.length) {
      props.showModal({ text: languageTexts.ambassador_index_text_74 })
      return false
    }

    props.showModal({
      title:
        props.selectedTab.value == TAB_APPLIED
          ? languageTexts.ambassador_index_text_75
          : languageTexts.ambassador_index_text_77,
      text:
        props.selectedTab.value == TAB_APPLIED
          ? languageTexts.ambassador_index_text_76
          : languageTexts.ambassador_index_text_78,
      confirm: () => {
        rejectSelectedAmbassadors()
        props.closeModal()
      },
      cancel: () => {
        props.closeModal()
      },
    })
  }

  const CautionModal = (componentProps: {
    [key: string]: any
    selectedAmbassadors: any[]
    rejectedAmbassadors: any[]
    onConfirm: () => void
    onCancel: () => void
  }) => {
    const [doNotShowAgain, setDoNotShowAgain] = useState(false)

    return (
      <div className="flex flex-col justify-center align-center p-8 bg-white text-black">
        <div className="font-bold text-[22px]">{`다음으로 넘어갑니다.`}</div>
        <div className="mt-[15px] text-[15px] text-wrap">
          {`선정하지 않은 인플루언서는\n자동으로 미선정 처리됩니다.`}
        </div>
        <div className="mt-[15px] font-bold text-[15px]">
          <div>{`선정: ${componentProps.selectedAmbassadors.length}명`}</div>
          <div>{`미선정: ${componentProps.rejectedAmbassadors.length}명`}</div>
        </div>
        <div className="mt-[20px] flex flex-row gap-[10px]">
          <Checkbox
            checked={doNotShowAgain}
            text={"다시 보지 않기"}
            onClick={() => {
              setDoNotShowAgain(!doNotShowAgain)
            }}
          />
        </div>
        <div className="buttons flex flex-row mt-[23px] gap-[13px]">
          <Button
            className={"py-3 flex justify-center text-center text-base font-bold"}
            borderColor={"BLACK"}
            text={languageTexts.cancel}
            onClick={() => {
              componentProps.onCancel()
            }}
          />
          <Button
            className={"py-3 flex justify-center text-center text-base font-bold"}
            borderColor={"BLACK"}
            buttonColor={"BLACK"}
            textColor={"WHITE"}
            text={languageTexts.confirm}
            onClick={() => {
              if (doNotShowAgain) {
                props.setPreferences({
                  isAmbassadorApplicantsListCautionModalDisabled: true,
                })
              }
              componentProps.onConfirm()
            }}
          />
        </div>
      </div>
    )
  }

  const showSelectionConfirmModal = (applicants, selectedIds) => {
    const selectedAmbassadors = []
    const rejectedAmbassadors = []
    for (const applicant of applicants) {
      if (selectedIds.includes(applicant.userId)) {
        selectedAmbassadors.push(applicant)
      } else {
        rejectedAmbassadors.push(applicant)
      }
    }

    props.showModal({
      hideCloseButton: true,
      content: () => {
        return (
          <CautionModal
            selectedAmbassadors={selectedAmbassadors}
            rejectedAmbassadors={rejectedAmbassadors}
            onCancel={() => {
              props.closeModal()
            }}
            onConfirm={() => {
              props.closeModal()
              submit()
            }}
          />
        )
      },
    })
  }

  const showReselectionConfirmModal = () => {
    if (!selected.length) {
      props.showModal({ text: languageTexts.ambassador_index_text_81 })
      return false
    }

    props.showModal({
      title: languageTexts.ambassador_index_text_79,
      text: languageTexts.ambassador_index_text_80,
      confirm: () => {
        approveSelectedAmbassadors()
        props.closeModal()
      },
      cancel: () => {
        props.closeModal()
      },
    })
  }

  const renderAmbassadorApplyDecisionButtons = () => {
    const isLoading = isFetching || isSubmitting
    return (
      <div className="flex flex-col justify-center items-center">
        <Button
          textColor={"WHITE"}
          className={"px-10 py-5 text-center select-none"}
          style={{ flex: "initial", width: "auto", minWidth: "280px" }}
          textClassName={"font-bold text-sm"}
          buttonColor={isLoading ? "#E0E0E0" : "BLACK"}
          borderColor={isLoading ? "#E0E0E0" : "BLACK"}
          text={isLoading ? "잠시만 기다려주세요.." : `${selected.length}명 선정하고 다음으로 넘어가기`}
          onClick={() => {
            if (isLoading) return
            if (props.preferences.isAmbassadorApplicantsListCautionModalDisabled != true) {
              showSelectionConfirmModal(applicants, selected)
              return
            }
            submit()
          }}
        />
        <div className="mt-4 flex flex-row items-center gap-1">
          <img className={"w-[22px] h-[22px] stroke-[2px] mx-1"} src={`/assets/icons/ic-ai.svg`} />
          <span className="text-[15px]">{languageTexts.ambassador_index_text_99}</span>
        </div>
      </div>
    )
  }

  const renderAmbassadorApprovedDecisionButtons = () => {
    return (
      <div className="flex flex-row items-center fixed bottom-0 left-auto right-0 md:left-auto px-5 py-6 form-submit-button z-[1]">
        <div className="relative z-10 ml-auto mr-auto my-3 text-right md:text-left md:my-0 md:ml-auto self-center"></div>

        <div className="mr-6"></div>

        {props.selectedTab.value == TAB_APPROVED && (
          <Button
            textColor={"BLACK"}
            className="flex px-3 py-3 items-center justify-center w-[240px] font-bold"
            buttonColor={"WHITE"}
            borderColor={"BLACK"}
            text={languageTexts.decline}
            onClick={() => {
              showRejectConfirmModal()
            }}
          />
        )}

        <div className="mr-[6px]"></div>

        {props.selectedTab.value == TAB_REJECTED && (
          <Button
            textColor={"WHITE"}
            className="flex px-3 py-3 items-center justify-center w-[240px] font-bold"
            buttonColor={"BLACK"}
            borderColor={"BLACK"}
            text={languageTexts.approve}
            onClick={() => {
              showReselectionConfirmModal()
            }}
          />
        )}
      </div>
    )
  }

  const renderUserSuggestionsRecommendationPanel = () => {
    const MINIMAL_COUNT_TO_SHOW = 30
    if (props.userSuggestionsCount < MINIMAL_COUNT_TO_SHOW) return null

    return (
      <div className="mt-[40px]">
        <div className="mx-auto py-[30px] w-[600px] border">
          <div className="text-[15px] font-bold text-center">{languageTexts.ambassador_index_text_100}</div>
          <div className="mt-[20px]">
            <Button
              textColor={"WHITE"}
              className="mx-auto px-4 py-3 max-w-[240px] flex justify-center items-center text-[15px] font-bold"
              buttonColor={"#000000"}
              borderColor={"#000000"}
              text={languageTexts.ambassador_index_text_101}
              onClick={async () => {
                requestSelectionToAdmin()
              }}
            />
          </div>
          <div className="mt-[20px]">
            <img
              width={80}
              className="mx-auto"
              src={`/assets/images/rental/ambassador-settings/user-suggestions-example.png`}
            />
          </div>
          <div className="mt-[10px] text-[15px] text-center">
            <span className="text-[#000000] font-bold">{`${languageTexts._num_of_person(
              props.userSuggestionsCount
            )}`}</span>
            <span>{`${languageTexts.ambassador_index_text_102}`}</span>
          </div>
        </div>
        <ModalContactManager
          avatarSrc="/assets/images/manager/manager-mikyeong.png"
          name=""
          onOk={() => {
            setIsVisibleModalContactManager(false)
          }}
          onClose={() => {
            setIsVisibleModalContactManager(false)
          }}
          zIndex={1080}
          isVisible={isVisibleModalContactManager}
        />
      </div>
    )
  }

  const renderBottomGradient = () => {
    return <BottomGradient />
  }

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 mt-6 mb-8 gap-y-8 gap-x-4">
        {applicants.map((application, index) => {
          const isSelected = selected && selected.length ? selected.includes(application.userId) : false

          return (
            <ModelCard
              type={TYPES.AMBASSADOR}
              key={`applicant-model-card-${application.id}-${index}`}
              application={application}
              selected={isSelected}
              onClick={() => handleSelect(application)}
              onLike={() => {
                API.Like.store({
                  brandToken: props.auth.token,
                  subject: "Users",
                  subjectId: application.userId,
                })
                  .then((response) => {
                    const applicant = [...applicants].map((item) =>
                      item.userId == application.userId
                        ? {
                            ...item,
                            ...{
                              likes: response.data.result ? [true] : [],
                            },
                          }
                        : item
                    )

                    setApplicants(applicant)
                  })
                  .catch((error) => {
                    props.showModal({ text: error.message })
                  })
              }}
              nextUser={applicants[index + 1] ? applicants[index + 1].user : null}
              previousUser={applicants[index - 1] ? applicants[index - 1].user : null}
            />
          )
        })}
      </div>
      {props.selectedTab.value === TAB_APPLIED &&
        !isFetching &&
        applicants.length > 0 &&
        renderAmbassadorApplyDecisionButtons()}
      {props.selectedTab.value === TAB_APPLIED &&
        !isFetching &&
        applicants.length > 0 &&
        renderUserSuggestionsRecommendationPanel()}
      {props.selectedTab.value !== TAB_APPLIED && (
        <>
          {renderAmbassadorApprovedDecisionButtons()}
          {renderBottomGradient()}
        </>
      )}
    </>
  )
}

const LoadingIndicator = (props) => {
  const language = useLanguage()
  const languageTexts = { ...language.common, ...language.pages.rental }
  if (!props.isFetching) return null

  return (
    <div className="flex flex-col">
      <div className="flex flex-row mx-10 my-10 justify-center items-center">
        <div className="flex flex-row justify-center text-center my-5">
          <span className="prose prose-xl text-slate-400">{languageTexts.loading_1}</span>
        </div>
      </div>
    </div>
  )
}

const AmbassadorBudget = (props) => {
  const navigate = useNavigate()
  const { ambassadorCntApproved, maxBudgetRental } = props.brand
  const language = useLanguage()
  const languageTexts = { ...language.common, ...language.pages.rental }

  const getBudgetContent = (budget) => {
    // if (budget <= 69) {
    //   return {
    //     message: languageTexts.ambassador_index_text_82,
    //     description: languageTexts.ambassador_index_text_83,
    //     to: () => {
    //       navigate(ROUTES.RENTAL_PAYMENTS_INDEX.PATH)
    //       window.scrollTo(0, 0)
    //     },
    //   }
    // }

    const targetAmbassadorCount = 100
    const currentAmbassadorCount = ambassadorCntApproved

    return {
      message: languageTexts._ambassador_index_text_84(currentAmbassadorCount, targetAmbassadorCount),
      description: languageTexts.ambassador_index_text_85,
      to: undefined,
    }
  }

  const renderBudgetContent = () => {
    const budget = maxBudgetRental / 10000
    const { message, description, to } = getBudgetContent(budget)

    if (ambassadorCntApproved >= 100) return null

    return (
      <div
        className="flex flex-col"
        style={{
          background: "rgba(250, 112, 53, 0.1)",
        }}
      >
        <div className="flex flex-col gap-[8px] py-[15px] px-[20px]">
          <div className="flex flex-row">
            <span className="text-[#FF0099] text-[17px] font-bold">{message}</span>
          </div>
          <div className="flex flex-row gap-[10px]">
            <span className="text-[#FF0099] text-[14px] ">{description}</span>
            {to ? (
              <span
                className="text-[#FF0099] text-[14px] font-bold underline cursor-pointer"
                onClick={() => {
                  to()
                }}
              >
                {languageTexts.ambassador_index_text_86}
              </span>
            ) : null}
          </div>
        </div>
      </div>
    )
  }

  return <div>{renderBudgetContent()}</div>
}

export default connect(mapStateToProps, mapDispatchProps)(AmbassadorSettings)
